import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NilStatuses, type PersonValuationResponse } from '@on3/api';
import { On3IconLock, On3IconWarningTriangle } from '@on3/icons';
import { useAuth } from '@on3/ui-lib/index';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { AppBadges } from 'components/Elite/AppBadges/AppBadges';
import { RosterValue } from 'components/Icons/RosterValue';
import { On3NilValuation } from 'components/Svg/On3NilValuation';
import { TextLink } from 'components/UI/Link/TextLink';
import { Text } from 'components/UI/Typography/Text';
import useAthlete from 'contexts/AthleteProvider';
import Link from 'next/link';

import styles from './NilValuation.module.scss';

export const NilValuation = ({
  valuation,
  valuationChange,
  nilStatus,
  rosterValue,
}: PersonValuationResponse) => {
  const { user } = useAuth();
  const { athlete, athleteInPro } = useAthlete();
  const { isVerifiedPlayer } = useAuth();

  const { slug } = athlete || {};
  const isSuspended = nilStatus === NilStatuses.Suspended;
  const isFinalValuation = nilStatus === NilStatuses.Final || athleteInPro;
  const showClaimButton = isSuspended || isFinalValuation;

  const primarySport =
    athlete?.defaultSport?.key === 1 || athlete?.defaultSport?.key === 2;

  // const claimProfileLinkText = 'Athletes - Claim Profile';

  const utmParams = new URLSearchParams({
    utm_source: 'nil_player_module',
    utm_medium: 'web',
    utm_campaign: `${slugify(athlete?.name)}`,
    utm_content: 'lock',
  });
  const utmLink = `/subscribe/?${utmParams}`;

  const SubscribeToUnlock = () => (
    <Link className={styles.subscribeBtn} href={utmLink}>
      <On3IconLock className={styles.lock} />
      <span>Subscribe to Unlock</span>
    </Link>
  );

  const nilStatusText = () => {
    if (nilStatus === NilStatuses.Suspended) {
      return <span>Valuation Suspended</span>;
    }

    if (nilStatus === NilStatuses.Final) {
      return (
        <span>
          This player has exited college and is no longer eligible for Nil
          Valuations
        </span>
      );
    }
  };

  const isNegativeChange = valuationChange && Math.sign(valuationChange) === -1;
  const formattedValuationChange =
    !!valuationChange && formatCurrency(Math.abs(valuationChange));
  const isVerified = isVerifiedPlayer(athlete?.key);
  const moneyWantedLink = isVerified
    ? `/db/${athlete?.slug}/elite/network/roster-brand/?modal=nil-wanted`
    : `/recruits/join/`;
  const challengeLink = isVerified
    ? `/db/${athlete?.slug}/elite/network/roster-brand/?modal=nil-challenge`
    : `/recruits/join/`;

  return (
    <div
      className={clsx(styles.container, {
        [styles.secondarySport]: !primarySport,
      })}
    >
      <div
        className={clsx(styles.valuationContainer, {
          [styles.secondarySport]: !primarySport,
        })}
      >
        <div
          className={clsx(styles.valuationWrapper, {
            [styles.secondarySport]: !primarySport,
          })}
        >
          <div
            className={clsx(styles.rosterNilContainer, {
              [styles.secondarySport]: !primarySport,
            })}
          >
            <div className={styles.valuations}>
              <div className={styles.valuation}>
                <On3NilValuation
                  className={styles.titleIcon}
                  fill="#fff"
                  height={20}
                  width={165}
                />
                <div className={styles.valuationIconWrapper}>
                  {valuation ? (
                    <span className={styles.valueText}>
                      {formatCurrency(valuation)}
                    </span>
                  ) : (
                    <>
                      {nilStatus === NilStatuses.Suspended && (
                        <span>
                          <On3IconWarningTriangle height={40} width={40} />
                        </span>
                      )}
                      {user?.has ? (
                        <span className={styles.valueText}>-</span>
                      ) : (
                        <SubscribeToUnlock />
                      )}
                    </>
                  )}
                </div>
                {!!valuationChange && (
                  <Text className={styles.rosterNilWrapper} variant="subtitle1">
                    <span
                      className={clsx(styles.rosterNilTotal, {
                        [styles.negative]: isNegativeChange,
                      })}
                    >
                      {isNegativeChange ? '-' : '+'}
                      {formattedValuationChange}
                    </span>
                  </Text>
                )}
                {isFinalValuation && (
                  <p className={styles.finalText}>Final Valuation</p>
                )}
                {nilStatusText() && (
                  <p className={styles.nilStatusText}>{nilStatusText()}</p>
                )}
              </div>

              <div className={styles.valuation}>
                <RosterValue className={styles.titleIcon} fill="#fff" />
                {user?.has ? (
                  <span
                    className={clsx(styles.valueText, styles.rosterValueText)}
                  >
                    {rosterValue ? formatCurrency(rosterValue) : '-'}
                  </span>
                ) : (
                  <SubscribeToUnlock />
                )}
              </div>
            </div>
          </div>
          {(!showClaimButton || !primarySport) && (
            <div
              className={clsx(styles.buttonWrapper, {
                [styles.secondarySport]: !primarySport,
              })}
            >
              {/* TODO: Recruits - claim profile
              <TextLink
                className={styles.eliteLink}
                href={`/db/${slug}/link/`}
                underline="none"
                variant="subtitle1"
              >
                <span>{claimProfileLinkText}</span>
                <InfoOutlinedIcon height={20} width={20} />
              </TextLink> */}
              {user?.pk && <AppBadges />}
              <div
                className={clsx(styles.valuationWantedWrapper, {
                  [styles.secondarySport]: !primarySport,
                })}
              >
                <TextLink
                  className={styles.valuationWantedButton}
                  href={challengeLink}
                >
                  Challenge my Valuation
                </TextLink>
                <TextLink
                  className={styles.valuationWantedButton}
                  href={moneyWantedLink}
                >
                  $ Wanted
                </TextLink>
              </div>
            </div>
          )}
        </div>
      </div>
      <TextLink
        className={styles.aboutLink}
        href="/nil/news/about-on3-nil-valuation-per-post-value/"
        underline="none"
        variant="subtitle1"
      >
        <span>About nil valuations</span>
        <InfoOutlinedIcon height={20} width={20} />
      </TextLink>
    </div>
  );
};
